import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import Seo from "@/components/base/Seo";
import { formatNum, getCategory, getById, relatedManga } from "@/utils/manga";
import MangaCard from "@/components/feature/MangaCard";

function ChapterList({ count, title }: { count: number; title: string }) {
  const [open, setOpen] = useState(true);
  const items = Array.from({ length: Math.min(count, 12) }, (_, i) => count - i);
  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4"
        aria-expanded={open}
      >
        <h2 className="font-heading text-lg text-foreground-950">章节列表</h2>
        <span className="flex items-center gap-1 text-sm text-foreground-500">
          共{count}话
          <i className={`ri-arrow-down-s-line text-lg transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-2 border-t border-background-200/70 p-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((n) => (
            <button
              key={n}
              type="button"
              className="whitespace-nowrap rounded-lg bg-background-100 px-3 py-2 text-center text-xs text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
            >
              第{n}话 · {title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const manga = getById(id || "");
  const [collected, setCollected] = useState(false);

  if (!manga) {
    return (
      <MainLayout>
        <Seo title="未找到该漫画 - 歪歪漫画网" description="您访问的漫画作品不存在或已下架。" />
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-heading text-3xl text-foreground-950">未找到该作品</h1>
          <p className="mt-3 text-foreground-600">这部漫画可能已下架或链接有误，去看看其它作品吧。</p>
          <Link
            to="/rankings"
            className="mt-6 inline-flex items-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回排行榜
          </Link>
        </div>
      </MainLayout>
    );
  }

  const cat = getCategory(manga.category);
  const related = relatedManga(manga.id, 5);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: manga.title,
      description: manga.intro,
      dateModified: new Date().toISOString().slice(0, 10),
      author: { "@type": "Person", name: manga.author },
      inLanguage: "zh-CN",
      mainEntityOfPage: `https://panelpal.github.io/manga/${manga.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://panelpal.github.io/" },
        {
          "@type": "ListItem",
          position: 2,
          name: `${cat.name}漫画`,
          item: `https://panelpal.github.io/category/${cat.id}`,
        },
        { "@type": "ListItem", position: 3, name: manga.title, item: `https://panelpal.github.io/manga/${manga.id}` },
      ],
    },
  ];

  return (
    <MainLayout>
      <Seo
        title={`${manga.title} - ${cat.name}漫画 | 歪歪漫画网`}
        description={`${manga.title}：${manga.intro} 标签：${manga.tags.join("、")}。歪歪漫画网提供${cat.name}漫画分类大全、人气排行与免费阅读推荐。`}
        keywords={`歪歪漫画网,${manga.title},${cat.name}漫画,${manga.tags.join(",")},漫画推荐`}
        canonical={`/manga/${manga.id}`}
        jsonLd={jsonLd}
      />

      {/* 详情头图 */}
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${cat.id}`} className="hover:text-primary-600">{cat.name}漫画</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{manga.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 sm:flex-row">
            <div className="shrink-0">
              <div className="relative mx-auto w-48 overflow-hidden rounded-lg border border-background-200/70 bg-background-200 sm:w-56">
                <img src={manga.cover} alt={`${manga.title} 漫画封面`} className="aspect-[3/4] w-full object-cover object-top" />
                {manga.isFree && (
                  <span className="absolute left-2 top-2 rounded-md bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                    免费
                  </span>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">{manga.title}</h1>
                {manga.isNew && (
                  <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">新作</span>
                )}
                {manga.status === "已完结" && (
                  <span className="rounded-md bg-foreground-950 px-2 py-0.5 text-xs font-medium text-background-50">已完结</span>
                )}
              </div>
              <p className="mt-2 text-sm text-foreground-600">{cat.name} · {manga.author} · 全{manga.chapters}话</p>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1 text-lg font-semibold text-primary-600">
                  <i className="ri-star-fill text-secondary-500" />
                  {manga.rating.toFixed(1)}
                  <span className="text-xs font-normal text-foreground-500">读者评分</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-fire-line text-primary-500" />
                  人气 {formatNum(manga.popularity)}
                </div>
                <div className="text-sm text-foreground-600">
                  <span className="text-foreground-500">更新：</span>{manga.updateInfo}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {manga.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-700">
                    {t}
                  </span>
                ))}
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700">{manga.intro}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line" /> 开始阅读
                </button>
                <button
                  type="button"
                  onClick={() => setCollected((v) => !v)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-6 py-2.5 text-sm font-semibold transition-colors ${
                    collected
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={`${collected ? "ri-heart-fill" : "ri-heart-line"} `} />
                  {collected ? "已收藏" : "加入收藏"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 章节 + 相关推荐 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ChapterList count={manga.chapters} title={manga.title} />
          </div>
          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground-900">作品信息</h2>
              <dl className="mt-3 space-y-2.5 text-sm">
                {[
                  ["作者", manga.author],
                  ["分类", cat.name],
                  ["状态", manga.status],
                  ["章节", `${manga.chapters} 话`],
                  ["标签", manga.tags.join("、")],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <dt className="text-foreground-500">{k}</dt>
                    <dd className="text-right text-foreground-800">{v}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to={`/category/${cat.id}`}
                className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                查看更多{cat.name}漫画 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="border-t border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">你可能还喜欢</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {related.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
}